/**
 * @file   src\Pages\SignIn.tsx
 * @brief  SignIn page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Button, Link, useState, useNavigate, useEffect, Row, Col, useLocation, useParams } from '../utils/thirdpartyComponents';
import Carwashlogo from '../assets/images/CarWash-Logo.svg';
import CWInput from '../components/CWInput';
import '../styles/signin.scss';
import * as Yup from 'yup'
import { pageURLs } from '../utils/pageUrls';
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH } from '../utils/constants';
import { MessageToaster } from '../utils/toastUtil';
import axios from '../config/axiosConfig';
import { AuthApi } from '../utils/apiUrls';
import { userType } from '../utils/enums';
import CWPasswordInput from '../components/CWPasswordInput';
import { SET_PASSWORD_SCHEMA } from '../validations/setPasswordSchema';
import SmallLoader from '../components/SmallLoader';

const SetCWOPassword = (props: any) => {
    const navigate = useNavigate();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const token = query.get('token') ? query.get('token') : ''

    // Toast object creation.
    const toaster = new MessageToaster();

    // Initialize states for formdata
    const [formData, setFormData] = useState({
        newpassword: '',
        retypepassword: ''

    })
    const [loading, setLoading] = useState(false);

    // Handle errors
    const [errors, setErrors] = useState({
        newpassword: '',
        retypepassword: ''
    });

    // On password input
    const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // Form submit
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        setLoading(true)
        e.preventDefault()
        try {
            // Validate form data
            await SET_PASSWORD_SCHEMA.validate(formData, { abortEarly: false });

            const { data } = await axios.post(AuthApi.RESET_PASSWORD, {
                resetToken: token,
                password: formData.newpassword,
                confirmpassword: formData.retypepassword
            })
            if (data?.error == 0) {
                toaster.toastSuccess(data?.message)
                //redirect to carwash oner sign in
                navigate(`/`)
                setLoading(false)

            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error) {
            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false)

        }
    }

    return (
        <div>
            <div className="signin-bg"></div>
            <div className="signin-wrap d-flex">
                <div className="signin-container d-grid">
                    <div className="signin-hero d-flex align-items-start flex-column justify-content-center">
                        <img src={Carwashlogo} alt="Car Wash App" />
                        <h1>{props.userType === userType['SUPER-ADMIN'] ? 'Super Admin' : 'Car Wash Owner'} </h1>
                    </div>
                    <div className="signin-form-wrap password-form-wrap">
                        <h2>Set Password</h2>
                        <p className='sub'>You can set password for carwash owner.</p>
                        <form onSubmit={handleSubmit}>

                            <Col md={12}>
                                <CWPasswordInput
                                    id="newpassword"
                                    name="newpassword"
                                    type={'password'}
                                    label="New Password"
                                    showpass={<span />}
                                    value={formData.newpassword}
                                    onChange={onInputChange}
                                    maxLength={PASSWORD_MAX_LENGTH}
                                    isMandatory
                                    autoComplete='off'
                                    errorMessage={errors.newpassword}
                                />
                            </Col>


                            <Col md={12}>
                                <CWPasswordInput
                                    id="retypepassword"
                                    name="retypepassword"
                                    type={'password'}
                                    label="Re-Type Password"
                                    showpass={<span />}
                                    value={formData.retypepassword}
                                    onChange={onInputChange}
                                    maxLength={PASSWORD_MAX_LENGTH}
                                    isMandatory
                                    autoComplete='off'
                                    errorMessage={errors.retypepassword}
                                />
                            </Col>

                            <Button
                                title='Submit'
                                type="submit"
                                variant="primary"
                                className="primary-btn"
                            >
                                Submit
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
            {loading && <SmallLoader />}
        </div>
    )
}

export default SetCWOPassword;
